import {
  AGE_BRACKETS, AGE_BRACKET_LABELS_TR,
  GROUP_TYPES, GROUP_TYPE_LABELS_TR,
  GENDERS, GENDER_LABELS_TR,
  ORIENTATIONS, ORIENTATION_LABELS_TR,
  MEDIA_TYPES,
} from "../constants.js";
import { getAds, addAd, updateAd, uploadMedia, getMediaPublicUrl } from "../ads-state.js";
import { toast } from "./toast.js";
import { renderInsightsHtml, updateInsights } from "./insights-card.js";
import { renderSimulatorHtml, bindSimulator, refreshSimulator } from "./simulator-panel.js";

function escapeHtml(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
    .replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64) || `ad-${Date.now().toString(36)}`;
}

const ORIENT_TOOLTIP = {
  any: "Hem yatay hem dikey kiosk için uygun",
  portrait: "Pivot edilmiş dikey kioska özel — yatay kioska düşerse kenarlarda siyah bar olur",
  landscape: "Normal yatay kiosk için — dikey kioska düşerse kenarlarda siyah bar olur",
};

const MEDIA_TYPE_LABELS = { image: "Görsel", video: "Video", html: "HTML" };
const MEDIA_TYPE_HINTS = {
  image: "PNG / JPG / GIF / SVG / WebP — statik görsel",
  video: "MP4 / WebM — sessiz, otomatik döner",
  html: "Bir URL'i iframe'de aç (interaktif içerik)",
};

function chipCheckRow(name, values, labels, checked, opts = {}) {
  const cls = opts.compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  return values.map(v => `
    <label class="inline-flex items-center gap-1.5 ${cls} rounded-md bg-slate-800 hover:bg-slate-700 cursor-pointer border border-transparent has-[:checked]:border-cyan-500 has-[:checked]:bg-cyan-500/10 has-[:checked]:text-cyan-200 transition-colors">
      <input type="checkbox" name="${name}" value="${escapeHtml(v)}" ${checked.includes(v) ? "checked" : ""} class="accent-cyan-500"/>
      <span>${escapeHtml(labels[v] || v)}</span>
    </label>`).join("");
}

function chipRadioRow(name, values, labels, checked, tooltips = {}) {
  return values.map(v => `
    <label title="${escapeHtml(tooltips[v] || "")}" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-[11px] cursor-pointer border border-transparent has-[:checked]:border-cyan-500 has-[:checked]:bg-cyan-500/10 has-[:checked]:text-cyan-200 transition-colors">
      <input type="radio" name="${name}" value="${escapeHtml(v)}" ${v === checked ? "checked" : ""} class="accent-cyan-500"/>
      <span>${escapeHtml(labels[v] || v)}</span>
    </label>`).join("");
}

function section(title, hint, body) {
  return `
    <section class="space-y-2 pb-3 border-b border-slate-800/60 last:border-0">
      <div class="flex items-baseline justify-between gap-3">
        <h4 class="text-xs uppercase tracking-widest text-cyan-400 font-bold">${title}</h4>
        ${hint ? `<p class="text-[10px] text-slate-500 italic flex-1 text-right">${hint}</p>` : ""}
      </div>
      ${body}
    </section>`;
}

function modalHtml(ad) {
  const editing = !!ad;
  const a = ad || {};
  const ageVals = a.age_brackets || [];
  const genderVals = a.genders || [];
  const groupVals = a.group_types || [];
  const orient = a.orientation || "any";
  const mediaType = a.media_type || "image";

  const mediaSection = section(
    editing ? "📷 Medya" : "📷 1) Medyayı yükle",
    "Hangi dosya gösterilecek?",
    `
      <div class="space-y-2">
        <input type="file" name="media_file"
               accept="image/*,video/mp4,video/webm,.svg"
               class="w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-cyan-500 file:text-slate-950 file:font-bold file:cursor-pointer file:hover:bg-cyan-400 cursor-pointer text-slate-400" />
        <div id="media-current" class="text-[10px] text-slate-500">
          ${a.media_path ? `Mevcut: <span class="font-mono text-slate-400">${escapeHtml(a.media_path)}</span>` : "Henüz dosya seçilmedi"}
        </div>
        <div id="media-preview" class="${a.media_path ? '' : 'hidden'}">
          ${a.media_path ? `<img src="${escapeHtml(getMediaPublicUrl(a.media_path))}" class="max-h-40 rounded border border-slate-800"/>` : ""}
        </div>
      </div>
      <div class="grid grid-cols-2 gap-3 pt-1">
        <div class="space-y-1">
          <label class="text-[10px] uppercase tracking-widest text-slate-500">Medya tipi</label>
          <div class="flex flex-wrap gap-1.5">${chipRadioRow("media_type", MEDIA_TYPES, MEDIA_TYPE_LABELS, mediaType, MEDIA_TYPE_HINTS)}</div>
          <p class="text-[10px] text-slate-500 italic" id="media-type-hint">${MEDIA_TYPE_HINTS[mediaType]}</p>
        </div>
        <div class="space-y-1">
          <label class="text-[10px] uppercase tracking-widest text-slate-500">
            Yön
            <span class="text-cyan-400 text-[9px] ml-1 normal-case">(önemli)</span>
          </label>
          <div class="flex flex-wrap gap-1.5">${chipRadioRow("orientation", ORIENTATIONS, ORIENTATION_LABELS_TR, orient, ORIENT_TOOLTIP)}</div>
          <p class="text-[10px] text-slate-500 italic" id="orient-hint">${ORIENT_TOOLTIP[orient]}</p>
        </div>
      </div>`
  );

  const infoSection = section(
    editing ? "📝 Bilgi" : "📝 2) Başlık ve süre",
    "Reklam nasıl tanınsın?",
    `
      <div class="grid grid-cols-3 gap-3">
        <div class="col-span-2 space-y-1">
          <label class="text-[10px] uppercase tracking-widest text-slate-500">Başlık</label>
          <input name="title" required value="${escapeHtml(a.title || "")}"
                 placeholder="örn. Yaz İndirimleri 2026"
                 class="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5 text-sm focus:border-cyan-500 outline-none" />
        </div>
        <div class="space-y-1">
          <label class="text-[10px] uppercase tracking-widest text-slate-500">
            Süre (sn)
            <span class="text-slate-600 text-[9px] ml-1 normal-case">3-120</span>
          </label>
          <input name="duration_sec" type="number" min="3" max="120" required
                 value="${a.duration_sec || 15}"
                 class="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5 text-sm tabular-nums focus:border-cyan-500 outline-none" />
        </div>
      </div>
      <div class="grid grid-cols-3 gap-3 pt-1">
        <div class="col-span-2 space-y-1">
          <label class="text-[10px] uppercase tracking-widest text-slate-500">ID (otomatik)</label>
          <input name="id" required ${editing ? "readonly" : ""} value="${escapeHtml(a.id || "")}"
                 class="w-full bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5 text-sm font-mono ${editing ? "opacity-60 cursor-not-allowed" : ""}"
                 placeholder="başlık'tan türeyecek" />
          <p class="text-[10px] text-slate-500 italic">${editing ? "ID değiştirilemez" : "Başlık yazınca otomatik dolar — istersen elle değiştir"}</p>
        </div>
        <div class="space-y-1 flex items-end pb-1">
          <label class="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" name="enabled" ${a.enabled !== false ? "checked" : ""} class="accent-cyan-500 w-4 h-4"/>
            <span class="text-xs">Aktif (gösterilsin)</span>
          </label>
        </div>
      </div>`
  );

  const targetingSection = section(
    editing ? "🎯 Kime Gösterilecek" : "🎯 3) Kime gösterilecek?",
    "Hedef profil — boş bırakırsan herkese gösterilir",
    `
      <div class="space-y-3">
        <div class="space-y-1">
          <div class="flex items-center justify-between">
            <label class="text-[10px] uppercase tracking-widest text-slate-500">Yaş aralığı</label>
            <span class="text-[9px] text-slate-600">(çoklu seçim)</span>
          </div>
          <div class="flex flex-wrap gap-1.5">${chipCheckRow("age_brackets", AGE_BRACKETS, AGE_BRACKET_LABELS_TR, ageVals)}</div>
        </div>
        <div class="space-y-1">
          <div class="flex items-center justify-between">
            <label class="text-[10px] uppercase tracking-widest text-slate-500">Cinsiyet</label>
            <span class="text-[9px] text-slate-600">(boş = herkes)</span>
          </div>
          <div class="flex flex-wrap gap-1.5">${chipCheckRow("genders", GENDERS, GENDER_LABELS_TR, genderVals)}</div>
        </div>
        <details class="group">
          <summary class="cursor-pointer text-[11px] text-slate-500 hover:text-cyan-400 inline-flex items-center gap-1 select-none">
            <span class="group-open:rotate-90 transition-transform inline-block">▶</span>
            <span>Grup tipi (opsiyonel — kalabalık kompozisyonu için)</span>
          </summary>
          <div class="pt-2 space-y-1">
            <div class="flex flex-wrap gap-1.5">${chipCheckRow("group_types", GROUP_TYPES, GROUP_TYPE_LABELS_TR, groupVals, { compact: true })}</div>
            <p class="text-[10px] text-slate-500 italic">örn. "genç çift" seçersen sadece çiftler önünde göstereceğiz</p>
          </div>
        </details>
      </div>`
  );

  const advancedSection = `
    <section class="pb-3">
      <details class="group">
        <summary class="cursor-pointer text-xs uppercase tracking-widest text-cyan-400 font-bold hover:text-cyan-300 inline-flex items-center gap-1 select-none">
          <span class="group-open:rotate-90 transition-transform inline-block">▶</span>
          ⚙️ Gelişmiş (öncelik, saat aralığı, gösterim limiti)
        </summary>
        <div class="pt-3 grid grid-cols-4 gap-3">
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-widest text-slate-500">Öncelik</label>
            <input name="priority" type="number" min="1" max="10" value="${a.priority ?? 5}"
                   class="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm tabular-nums focus:border-cyan-500 outline-none"/>
            <p class="text-[9px] text-slate-500">1-10 (5 normal)</p>
          </div>
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-widest text-slate-500">Başlangıç</label>
            <input name="start_hour" type="number" min="0" max="23" value="${a.start_hour ?? 0}"
                   class="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm tabular-nums focus:border-cyan-500 outline-none"/>
            <p class="text-[9px] text-slate-500">0-23 saat</p>
          </div>
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-widest text-slate-500">Bitiş</label>
            <input name="end_hour" type="number" min="0" max="24" value="${a.end_hour ?? 24}"
                   class="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm tabular-nums focus:border-cyan-500 outline-none"/>
            <p class="text-[9px] text-slate-500">0-24 saat</p>
          </div>
          <div class="space-y-1">
            <label class="text-[10px] uppercase tracking-widest text-slate-500">Günlük max</label>
            <input name="max_impressions_per_day" type="number" min="1" max="10000" value="${a.max_impressions_per_day || ""}"
                   placeholder="sınırsız"
                   class="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm tabular-nums focus:border-cyan-500 outline-none"/>
            <p class="text-[9px] text-slate-500">boş = sınırsız</p>
          </div>
        </div>
      </details>
    </section>`;

  return `
    <div class="fixed inset-0 z-40 modal-backdrop flex items-center justify-center p-4" id="form-modal-bg">
      <div class="bg-slate-900 rounded-lg w-full max-w-6xl max-h-[94vh] overflow-hidden flex flex-col border border-slate-800">
        <div class="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-slate-900/80">
          <div>
            <h3 class="font-bold text-lg">${editing ? "Reklamı Düzenle" : "Yeni Reklam"}</h3>
            <p class="text-[11px] text-slate-500">${editing ? "Değişiklikleri kaydedince tüm ekiplere otomatik yansır." : "3 adım: medya → bilgi → kime gösterilecek. Hedefleme zorunlu değil."}</p>
          </div>
          <button type="button" data-close class="text-slate-400 hover:text-white text-2xl leading-none">&times;</button>
        </div>
        <div class="overflow-y-auto grid grid-cols-12 gap-4 px-5 py-4">
          <form id="ad-form" class="col-span-12 lg:col-span-7 space-y-4">
            ${mediaSection}
            ${infoSection}
            ${targetingSection}
            ${advancedSection}
          </form>
          <div class="col-span-12 lg:col-span-5 space-y-3 lg:sticky lg:top-0 lg:self-start">
            ${renderInsightsHtml()}
            ${renderSimulatorHtml()}
          </div>
        </div>
        <div class="px-5 py-3 border-t border-slate-800 flex justify-between items-center gap-2 bg-slate-900/60">
          <p class="text-[10px] text-slate-500">
            ${editing ? "ID değişmez. Diğer alanlar serbest." : "Eksik alan kalırsa varsayılan değerle kaydedilir."}
          </p>
          <div class="flex gap-2">
            <button type="button" data-close class="px-3 py-1.5 rounded text-xs uppercase tracking-widest text-slate-400 hover:text-white">İptal</button>
            <button type="button" id="ad-save-btn" class="px-4 py-1.5 rounded bg-cyan-500 text-slate-950 text-xs uppercase tracking-widest font-bold hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed">
              ${editing ? "Kaydet" : "Oluştur"}
            </button>
          </div>
        </div>
      </div>
    </div>`;
}

async function handleSave(ad, editing) {
  const form = document.getElementById("ad-form");
  const fd = new FormData(form);
  const saveBtn = document.getElementById("ad-save-btn");

  const id = (fd.get("id") || "").toString().trim() || slugify(fd.get("title"));
  const title = (fd.get("title") || "").toString().trim();
  if (!title) {
    toast("Başlık boş olamaz", "error");
    return;
  }

  const startH = parseInt(fd.get("start_hour"), 10) || 0;
  const endH = parseInt(fd.get("end_hour"), 10);
  const endHour = Number.isFinite(endH) ? endH : 24;
  if (startH === endHour) {
    toast("Başlangıç ve bitiş saati aynı — reklam asla oynamaz", "error");
    return;
  }

  saveBtn.disabled = true;
  saveBtn.textContent = "Kaydediliyor…";

  let mediaPath = ad ? ad.media_path : null;
  const file = form.querySelector('input[name="media_file"]').files[0];
  if (file) {
    try {
      mediaPath = await uploadMedia(file);
    } catch (err) {
      toast("Medya yüklenemedi: " + (err.message || "?"), "error");
      saveBtn.disabled = false;
      saveBtn.textContent = editing ? "Kaydet" : "Oluştur";
      return;
    }
  }

  const payload = {
    id: editing ? ad.id : id,
    title,
    media_path: mediaPath,
    media_type: fd.get("media_type") || "image",
    orientation: fd.get("orientation") || "any",
    duration_sec: parseInt(fd.get("duration_sec"), 10) || 15,
    age_brackets: fd.getAll("age_brackets"),
    genders: fd.getAll("genders"),
    group_types: fd.getAll("group_types"),
    priority: parseInt(fd.get("priority"), 10) || 5,
    start_hour: startH,
    end_hour: endHour,
    enabled: fd.get("enabled") === "on",
    max_impressions_per_day: fd.get("max_impressions_per_day")
      ? parseInt(fd.get("max_impressions_per_day"), 10)
      : null,
  };

  try {
    if (editing) {
      await updateAd(ad.id, payload);
      toast("Güncellendi", "ok");
    } else {
      await addAd(payload);
      toast("Eklendi — ekibin de görüyor", "ok");
    }
    closeFormModal();
  } catch (err) {
    toast(err.message || "Kayıt hatası", "error");
    saveBtn.disabled = false;
    saveBtn.textContent = editing ? "Kaydet" : "Oluştur";
  }
}

export function openFormModal(id) {
  const ads = getAds();
  const ad = id ? ads.find((a) => a.id === id) : null;
  const host = document.getElementById("form-modal-host");
  host.innerHTML = modalHtml(ad);

  host.querySelectorAll("[data-close]").forEach((btn) =>
    btn.addEventListener("click", closeFormModal)
  );
  const bg = document.getElementById("form-modal-bg");
  bg.addEventListener("click", (e) => {
    if (e.target.id === "form-modal-bg") closeFormModal();
  });

  document.getElementById("ad-save-btn").addEventListener("click", () =>
    handleSave(ad, !!ad)
  );

  // Title değiştikçe slug auto-fill (sadece yeni reklam için)
  if (!ad) {
    const titleInput = host.querySelector('input[name="title"]');
    const idInput = host.querySelector('input[name="id"]');
    let manuallyEdited = false;
    idInput.addEventListener("input", () => { manuallyEdited = true; });
    titleInput.addEventListener("input", () => {
      if (!manuallyEdited) idInput.value = slugify(titleInput.value);
    });
  }

  // Medya tipi/yön ipuçlarını canlı güncelle
  host.querySelectorAll('input[name="media_type"]').forEach((r) => {
    r.addEventListener("change", () => {
      const v = r.value;
      const hint = document.getElementById("media-type-hint");
      if (hint) hint.textContent = MEDIA_TYPE_HINTS[v] || "";
    });
  });
  host.querySelectorAll('input[name="orientation"]').forEach((r) => {
    r.addEventListener("change", () => {
      const v = r.value;
      const hint = document.getElementById("orient-hint");
      if (hint) hint.textContent = ORIENT_TOOLTIP[v] || "";
    });
  });

  // Canlı insights + simülasyon panel: her form değişikliğinde re-render
  const formEl = host.querySelector("#ad-form");
  updateInsights(formEl);
  bindSimulator(formEl);
  formEl.addEventListener("input", () => {
    updateInsights(formEl);
    refreshSimulator(formEl);
  });
  formEl.addEventListener("change", () => {
    updateInsights(formEl);
    refreshSimulator(formEl);
  });

  // Medya preview update + media_type auto-select dosyaya göre
  const fileInput = host.querySelector('input[name="media_file"]');
  const preview = host.querySelector("#media-preview");
  fileInput.addEventListener("change", () => {
    const f = fileInput.files[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    preview.classList.remove("hidden");
    if (f.type.startsWith("video/")) {
      preview.innerHTML = `<video src="${url}" muted autoplay loop playsinline class="max-h-40 rounded border border-slate-800"></video>`;
      const r = host.querySelector('input[name="media_type"][value="video"]');
      if (r) {
        r.checked = true;
        const hint = document.getElementById("media-type-hint");
        if (hint) hint.textContent = MEDIA_TYPE_HINTS.video;
      }
    } else {
      preview.innerHTML = `<img src="${url}" class="max-h-40 rounded border border-slate-800"/>`;
      const r = host.querySelector('input[name="media_type"][value="image"]');
      if (r) {
        r.checked = true;
        const hint = document.getElementById("media-type-hint");
        if (hint) hint.textContent = MEDIA_TYPE_HINTS.image;
      }
    }
  });
}

export function closeFormModal() {
  const host = document.getElementById("form-modal-host");
  if (host) host.innerHTML = "";
}
